import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { DevSettings, Platform } from "@-/com.npmjs.react-native";
import * as Updates from "@-/com.npmjs.expo-updates";
import { getRuntimeScopedState } from "@-/dev.improve.haniverse.lib.runtime-scoped-state";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Restart is the last-resort escape hatch of every error screen, so it must itself be unable to
 * hang. A hook gets this long to release what it owns; past it, native teardown releases the rest.
 */
const BEFORE_RESTART_HOOK_DEADLINE_MS = 5000;
/** `Updates.reloadAsync` normally never returns (the runtime is replaced); past this it failed. */
const UPDATES_RELOAD_DEADLINE_MS = 10_000;

type BeforeRestartHook = () => Promise<void> | void;

// Runtime-scoped so a Fast Refresh of a registering module replaces its hook
// instead of adding a second copy.
function getBeforeRestartHooks(): Map<string, BeforeRestartHook> {
  return getRuntimeScopedState(
    "app.restart.before-restart-hooks",
    () => new Map<string, BeforeRestartHook>(),
  );
}

/**
 * Register work that must finish before the JavaScript runtime is replaced —
 * closing owned resources such as database connections, so nothing native is left
 * mid-operation when its JavaScript owner disappears. Keyed by name: registering
 * the same name again replaces the previous hook.
 */
export function registerBeforeRestart(name: string, hook: BeforeRestartHook): void {
  getBeforeRestartHooks().set(name, hook);
}

async function runBeforeRestartHooks(): Promise<void> {
  for (const [name, hook] of getBeforeRestartHooks()) {
    try {
      await withDeadline(
        { deadline: BEFORE_RESTART_HOOK_DEADLINE_MS, operation: `before-restart hook "${name}"` },
        async () => hook(),
      );
    } catch (error) {
      // A restart must never be blocked by cleanup: native teardown releases what a
      // failed hook could not, and the log is the evidence of what was skipped.
      logger(import.meta.location).warn(
        `before-restart hook "${name}" failed; restarting anyway`,
        error,
      );
    }
  }
}

/**
 * The ONE way to restart the JavaScript runtime. Every reload path in the app goes
 * through here (the lint config bans `Updates.reloadAsync` / `DevSettings.reload`
 * elsewhere) so registered owners always get to close before the runtime that owns
 * them is thrown away — a reload must never leave native work orphaned.
 *
 * Tries every mechanism this build might have. `Updates.reloadAsync()` alone is NOT
 * enough and must never be the only path: expo-updates is misconfigured on staging
 * builds (the same reason the actor sign-in swap is reload-free — see
 * `use-actor-database.ts`), so its promise rejects and nothing happens. That is
 * precisely how a recoverable one-off error turned into a permanently bricked app:
 * the fallback's only button was a no-op.
 *
 * Lives in its own package rather than inside the root layout because it is the
 * last-resort escape hatch for EVERY error fallback, not just the root one — the
 * page-level `SuspensePageBoundary` needs the same guarantee that its buttons can
 * never be no-ops.
 */
export async function restartApp(): Promise<void> {
  await runBeforeRestartHooks();
  if (Platform.OS === "web") {
    globalThis.location.reload();
    return;
  }
  try {
    await withDeadline(
      { deadline: UPDATES_RELOAD_DEADLINE_MS, operation: "Updates.reloadAsync" },
      () => Updates.reloadAsync(),
    );
  } catch (error) {
    logger(import.meta.location).warn(
      "Updates.reloadAsync failed; falling back to DevSettings.reload",
      error,
    );
    if (typeof DevSettings.reload === "function") {
      DevSettings.reload();
      return;
    }
    throw error;
  }
}
